import React, { useEffect, useState } from "react";
import "react-tooltip/dist/react-tooltip.css";
import { Tooltip } from "react-tooltip";
import { IDocDetails } from "../../interfaces/IDocDetails";
import { ChatAI } from "./components/ChatAI/ChatAI";
import { useTranslation } from "react-i18next";
import { cleanMarkdownData } from "./helpers/cleanMarkdownData";
import { InfoTab } from "./components/InfoTab/InfoTab";
import { SummaryTab } from "./components/SummaryTab/SummaryTab";
import { useMutation } from "@tanstack/react-query";
import { updateDocumentInfo } from "./services/updateDocumentInfo.service";
import { toast } from "react-toastify";
import { translateFields } from "../../services/translateFields.service";
import { useUserContext } from "../../../../context/UserContext";
import { canUserEditDoc } from "./helpers/userRightsEditDoc";
import { CommentTab } from "./components/CommentTab/CommentTab";
import { useParams } from "react-router-dom";

interface ISummaryBlockProps {
  data: IDocDetails;
  onFetchedLink: (url: string) => void;
}

const SummaryInfoBlock: React.FC<ISummaryBlockProps> = ({
  data,
  onFetchedLink,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("Summary");
  const [editableData, setEditableData] = useState(cleanMarkdownData(data));
  const [editMode, setEditMode] = useState({
    classes: false,
    decision: false,
    short_summary: false,
    summary: false,
    provisions: false,
    participants: false,
    keywords: false,
    promulgation: false,
    subject_matter: false,
    tech_product: false,
    tech_section_vat_act: false,
    title: false,
    tags: false,
    tax_alert: false,
	date: false,
	date_issue: false,
  });
  const [isTranslatedFields, setIsTranslatedFields] = useState(false);
  const [targetLang, setTargetLang] = useState("");
  const [isUnsavedModalOpen, setIsUnsavedModalOpen] = useState(false);
  const [pendingTab, setPendingTab] = useState<string | null>(null);
  const [discardSignal, setDiscardSignal] = useState(0);
  const [infoDiscardSignal, setInfoDiscardSignal] = useState(0);
  const [pendingDiscardType, setPendingDiscardType] = useState<
    "summary" | "info" | null
  >(null);
  const originalFields = {
    decision: data.decision,
    short_summary: data.short_summary,
    summary: data.summary,
  };
  const { user } = useUserContext();
  const { regulator } = useParams<{ regulator: string }>();

  const canViewComments = React.useMemo(() => {
    if (!regulator) return false;
    return (
      user?.roles?.some((r) =>
        r?.team?.regulators?.some((g: any) => g?.code === regulator),
      ) ?? false
    );
  }, [user, regulator]);

  const { mutate: updateDoc } = useMutation({
    mutationFn: async (data: Partial<IDocDetails>) =>
      await updateDocumentInfo(editableData.code, editableData.id, data),
    onSuccess: () => {
      toast.success(t("docDetails.saveDocInfoToaster"));
    },
    onError: (error: Error) => {
      console.error("Error updating document info:", error);
    },
  });

  const { mutate: translateFieldsMutation, isPending: isTranslateLoading } =
    useMutation<any, Error, string>({
      mutationFn: async (language: string) =>
        await translateFields(
          {
            decision: editableData.decision || "",
            short_summary: editableData.short_summary || "",
            summary: editableData.summary || "",
          },
          language,
        ),
      onSuccess: (translatedData: any) => {
        setTargetLang(translatedData.target_lang);
        setEditableData((prev) => ({
          ...prev,
          ...(translatedData.decision !== undefined && {
            decision: translatedData.decision,
          }),
          ...(translatedData.short_summary !== undefined && {
            short_summary: translatedData.short_summary,
          }),
          ...(translatedData.summary !== undefined && {
            summary: translatedData.summary,
          }),
        }));
        setIsTranslatedFields(true);

        toast.success(t("translateModal.translationTooltip"));
      },
      onError: (error: Error) => {
        console.error("Error translating document fields:", error);
      },
    });

  const handleTranslate = (lang: string) => {
    translateFieldsMutation(lang);
  };

  const onShowOriginal = () => {
    if (isTranslatedFields) {
      setEditableData((prev) => ({
        ...prev,
        ...(originalFields.decision !== undefined && {
          decision: originalFields.decision,
        }),
        ...(originalFields.short_summary !== undefined && {
          short_summary: originalFields.short_summary,
        }),
        ...(originalFields.summary !== undefined && {
          summary: originalFields.summary,
        }),
      }));
      setIsTranslatedFields(false);

      toast.success(t("translateModal.translationTooltip"));
    }
  };

  useEffect(() => {
    const hasSummary =
      editableData.short_summary?.trim() || editableData.summary?.trim();

    setActiveTab(hasSummary ? "Summary" : "Information");
  }, []);

  const hasUnsavedSummary = Boolean(editMode.summary || editMode.short_summary);
  const hasUnsavedInfo = Object.entries(editMode).some(
    ([key, value]) => value && key !== "summary" && key !== "short_summary",
  );

  const requestTabChange = (tab: string) => {
    if (activeTab === "Summary" && hasUnsavedSummary) {
      setPendingTab(tab);
      setPendingDiscardType("summary");
      setIsUnsavedModalOpen(true);
      return;
    }
    if (activeTab === "Information" && hasUnsavedInfo) {
      setPendingTab(tab);
      setPendingDiscardType("info");
      setIsUnsavedModalOpen(true);
      return;
    }
    setActiveTab(tab);
  };

  const discardSummaryChanges = () => {
    setEditMode((prev) => ({
      ...prev,
      summary: false,
      short_summary: false,
    }));
    setEditableData((prev) => ({
      ...prev,
      summary: originalFields.summary ?? prev.summary,
      short_summary: originalFields.short_summary ?? prev.short_summary,
    }));
    setDiscardSignal((prev) => prev + 1);
  };

  const discardInfoChanges = () => {
    setInfoDiscardSignal((prev) => prev + 1);
  };

  const confirmDiscard = () => {
    if (pendingDiscardType === "summary") {
      discardSummaryChanges();
    }
    if (pendingDiscardType === "info") {
      discardInfoChanges();
    }
    setIsUnsavedModalOpen(false);
    setPendingDiscardType(null);
    if (pendingTab) {
      setActiveTab(pendingTab);
      setPendingTab(null);
    }
  };

  const keepEditing = () => {
    setIsUnsavedModalOpen(false);
    setPendingTab(null);
    setPendingDiscardType(null);
  };

  return (
    <div className="bg-base-color flex flex-col h-full">
      <div className="px-3 pt-3">
        <div className="flex justify-between rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-200/80 dark:bg-slate-800/60 p-1">
          <button
            className={`flex-1 text-center px-3 py-2 text-base font-normal rounded-lg transition ${
              activeTab === "Summary"
                ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow border border-slate-200 dark:border-slate-700"
                : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
            }`}
            onClick={() => requestTabChange("Summary")}
          >
            {t("docDetails.summary")}
          </button>
          <button
            className={`flex-1 text-center px-3 py-2 text-base font-normal rounded-lg transition ${
              activeTab === "Information"
                ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow border border-slate-200 dark:border-slate-700"
                : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
            }`}
            onClick={() => requestTabChange("Information")}
          >
            {t("docDetails.information")}
          </button>
          {canViewComments && (
            <button
              className={`flex-1 text-center px-3 py-2 text-base font-normal rounded-lg transition ${
                activeTab === "TeamComments"
                  ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow border border-slate-200 dark:border-slate-700"
                  : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
              }`}
              onClick={() => requestTabChange("TeamComments")}
            >
              {t("docDetails.comments")}
            </button>
          )}
          <button
            className={`flex-1 text-center px-3 py-2 text-base font-normal rounded-lg transition ${
              activeTab === "ChatAI"
                ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow border border-slate-200 dark:border-slate-700"
                : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
            }`}
            onClick={() => requestTabChange("ChatAI")}
          >
            <span>
              {t("docDetails.chatAI")}
              <span className="text-del-green font-semibold text-xs -top-2 relative">
                AI
              </span>
            </span>
          </button>
        </div>
      </div>

      <div
        className={`flex-grow pt-2 min-h-0 ${
          activeTab === "TeamComments" || activeTab === "ChatAI"
            ? "overflow-hidden flex flex-col"
            : "overflow-y-auto"
        }`}
      >
        {activeTab === "TeamComments" && canViewComments && (
          <div className="p-1 flex-1 min-h-0">
            <CommentTab user={user} />
          </div>
        )}

        {activeTab === "Information" && (
          <div className="pt-2 px-3">
            <InfoTab
              editableData={editableData}
              editMode={editMode}
              setEditableData={setEditableData}
              setEditMode={setEditMode}
              updatedData={(data: Partial<IDocDetails>) => updateDoc(data)}
              onFetchedLink={(url: string) => onFetchedLink(url)}
              handleTranslate={handleTranslate}
              isTranslatedFields={isTranslatedFields}
              loading={isTranslateLoading}
              onShowOriginal={onShowOriginal}
              canUserEdit={canUserEditDoc(user, data.code)}
              discardSignal={infoDiscardSignal}
            />
          </div>
        )}

        {activeTab === "Summary" && (
          <div className="pt-2 px-3">
            <SummaryTab
              editableData={editableData}
              editMode={editMode}
              setEditableData={setEditableData}
              setEditMode={setEditMode}
              updatedData={(data: Partial<IDocDetails>) => updateDoc(data)}
              handleTranslate={handleTranslate}
              isTranslatedFields={isTranslatedFields}
              loading={isTranslateLoading}
              onShowOriginal={onShowOriginal}
              canUserEdit={canUserEditDoc(user, data.code)}
              discardSignal={discardSignal}
            />
          </div>
        )}

        <div
          className={`${
            activeTab === "ChatAI" ? "block flex-1 min-h-0" : "hidden"
          }`}
        >
          <ChatAI isDataFound={Object.keys(editableData).length !== 0} />
        </div>
      </div>

      {activeTab !== "TeamComments" && (
        <div className="flex-shrink-0 border-t border-gray-300 dark:border-gray-700 p-2 text-xs text-gray-600 dark:text-gray-400 text-center">
          {t("aiDisclaimer")}
        </div>
      )}

      {isUnsavedModalOpen && (
        <div className="modal-overlay">
          <div className="modal-panel p-5 w-full max-w-md">
            <div className="flex items-start justify-between">
              <h4 className="text-base font-semibold">
                {t("docDetails.unsavedTitle", {
                  defaultValue: "Unsaved changes",
                })}
              </h4>
              <button
                className="modal-close-btn"
                onClick={keepEditing}
                aria-label={t("docDetails.unsavedKeep", {
                  defaultValue: "Keep editing",
                })}
              >
                ×
              </button>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              {t("docDetails.unsavedBody", {
                defaultValue:
                  "You have unsaved summary changes. Discard them to switch tabs.",
              })}
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={keepEditing}
                className="px-3 py-1.5 text-sm font-medium text-slate-700 border border-slate-200 rounded-md hover:bg-slate-50"
              >
                {t("docDetails.unsavedKeep", {
                  defaultValue: "Keep editing",
                })}
              </button>
              <button
                type="button"
                onClick={confirmDiscard}
                className="px-3 py-1.5 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600"
              >
                {t("docDetails.unsavedDiscard", {
                  defaultValue: "Discard changes",
                })}
              </button>
            </div>
          </div>
        </div>
      )}

      <Tooltip
        anchorSelect=".tooltip-open-outputAdj"
        place="top"
        className="text-xs font-light z-50"
        content={t("chatAI.openOutputAdjTooltip")}
      />
    </div>
  );
};

export { SummaryInfoBlock };
